import { MFA_COUNTDOWN_STARTED_AT } from '@/constants/system';

// The single authority on the MFA resend cooldown (PD-2932).
//
// WHY A FRONTEND CONSTANT: the login contract carries no cooldown anywhere. The
// send and resend replies are the same `LoginResponse`, whose MFA challenge has
// exactly one field (`MfaChallenge = { destMasked }`, api.types.ts), and the 429
// that rate limiting produces comes back WITHOUT a `Retry-After` header. So 60
// is ours to hold, identical for every tenant — deliberately NOT read from
// `useRuntimeConfig()`, single build or not.
//
// WHAT IT IS NOT: the real limiter is server side and coarser — at most 5 codes
// per account per 600s window, and the first code (the one `POST /v1/login`
// sends) already spends one of the five. This cooldown is the UX guard rail that
// stops a user burning the remaining four on impatient double-clicks; it is not
// a security boundary and must never be treated as one.
export const MFA_RESEND_COOLDOWN_SECONDS = 60;

// Record that a verification code has just gone out. Called from the two — and
// only two — places where that actually happens:
//   1. login-step.ts, pending branch with `next === STEP_MFA` (the first code,
//      sent by POST /v1/login before the user ever reaches /login-sms)
//   2. login-sms.tsx `startCounter` (POST /v1/login/mfa, every resend)
// A later code overwrites an earlier one, which is right: the server overwrites
// the stored OTP too, so the previous code is dead the moment a new one is sent.
export function armMfaResendCountdown(now: number = Date.now()): void {
  localStorage.setItem(MFA_COUNTDOWN_STARTED_AT, String(now));
}

export function clearMfaResendCountdown(): void {
  localStorage.removeItem(MFA_COUNTDOWN_STARTED_AT);
}

// Seconds still left on the cooldown. `0` means "not cooling down" — no code has
// been sent, or the 60s are up. Callers derive the disabled state from `> 0`, so
// there is no second flag that can disagree with this number.
//
// Recomputed from the stored instant on every tick rather than decremented, so a
// slept laptop / throttled background tab resumes at the correct value instead of
// continuing from where it froze.
export function readMfaResendRemaining(now: number = Date.now()): number {
  const startedAt = localStorage.getItem(MFA_COUNTDOWN_STARTED_AT);
  if (!startedAt) {
    return 0;
  }

  const parsed = Number(startedAt);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    // Junk value (hand-edited, or a shape from some future build). Treat as no
    // cooldown and drop it rather than letting NaN arithmetic decide the UI.
    clearMfaResendCountdown();
    return 0;
  }

  const elapsed = Math.floor((now - parsed) / 1000);
  const remaining = MFA_RESEND_COOLDOWN_SECONDS - elapsed;

  if (remaining <= 0) {
    // Expired. Purge on read: this is the unconditional lifecycle terminus that
    // kills the stale timestamps older builds left behind forever (they are the
    // reason a returning browser used to render a greyed-out `Resend(0s)` for one
    // frame). Without it, `advanceLoginStep`'s clear-on-landing would only ever
    // help users who complete a login on the new build first.
    clearMfaResendCountdown();
    return 0;
  }

  // Clock moved backwards (NTP correction, user changed the system clock), so the
  // stored instant looks like the future. Clamp instead of showing 3000s.
  return Math.min(remaining, MFA_RESEND_COOLDOWN_SECONDS);
}
